// SPDX-FileCopyrightText: 2026 Angels' Ware
// SPDX-License-Identifier: AGPL-3.0-or-later

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, FilePlus2, LayoutTemplate, Play, Sparkles } from "lucide-react";
import { FlowIcon, ICON } from "../icons";
import { api } from "../api";
import { useAuth } from "../auth";
import { loadRecentFlow, userScope } from "../recentFlow";

// Welcome is the post-signup landing — the "first-run" surface.
//
// It reads top to bottom as: who you are, where you were, what you can do next.
// That ordering is the whole rework: before it, the page opened with a greeting,
// offered the resume link, and only THEN introduced itself, so the one line
// explaining the page arrived after the reader had been given something to
// click.
//
// The four ways into a new flow all go to /flows/new, differing only in ?tab=,
// but they were presented at three different weights — and describing a flow in
// English, the most distinctive of them, was the one at the bottom in a box.
// They are now one row of equal cards carrying the SAME icons as the tabs they
// land on, so the thing you pressed is the thing you arrive at.
//
// The resume card survives untouched: it is the only element here that knows
// anything about this particular user, which is why it outranks every generic
// call to action.

// HAS_FLOWS_KEY mirrors App.tsx's RootRedirect signal. We read it to decide
// between first-run and returning copy: someone who has already built a flow
// gets a quieter headline instead of the onboarding tone.
const HAS_FLOWS_KEY = "dazyflow.hasFlows";

// The three ways to start something new, in the order CreateFlow's tabs use.
// Icons match that tab strip on purpose — LayoutTemplate / Sparkles /
// FilePlus2 — so the card and the page it opens are recognisably the same
// route rather than two designs of the same idea.
const OPTIONS = [
  { key: "template", to: "/flows/new?tab=template", Icon: LayoutTemplate },
  { key: "ai", to: "/flows/new?tab=ai", Icon: Sparkles },
  { key: "blank", to: "/flows/new?tab=blank", Icon: FilePlus2 },
] as const;

export function Welcome() {
  const { t } = useTranslation();
  const { me, token, activeTenant, activeWorkspace } = useAuth();
  // Whether "describe it in plain English" can actually do anything yet.
  // That card is the most distinctive of the three and the one the marketing
  // leads with, but it needs a connected AI provider — so on a fresh account
  // it took the reader to a dead end with the requirement only revealed after
  // they'd committed. undefined = still checking: say nothing rather than
  // flash a warning that turns out to be wrong.
  const [aiReady, setAiReady] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    if (!token) return;
    let live = true;
    try {
      Promise.resolve(api.listLLMProviders(token))
        .then((r) => live && setAiReady((r?.providers ?? []).length > 0))
        .catch(() => live && setAiReady(true));
    } catch {
      setAiReady(true);
    }
    return () => {
      live = false;
    };
  }, [token]);
  // Scoped to the signed-in account AND the active org — an unscoped read
  // offered another user's flow on a shared browser, and keying on the home
  // tenant surfaced the previous org's flow after a switch. Recomputed when
  // `me` / activeTenant resolve.
  const recent = loadRecentFlow(userScope(activeTenant || me?.tenant, me?.subject));
  const recentId = recent?.id;
  const [live, setLive] = useState<{ icon?: string; name?: string } | null>(null);
  useEffect(() => {
    if (!recentId || !token || !activeWorkspace) return;
    let cancelled = false;
    api
      .listGraphs(token, activeTenant, activeWorkspace)
      .then((r) => {
        if (cancelled) return;
        const f = r.graphs.find((g) => g.id === recentId);
        if (f) setLive({ icon: f.icon, name: f.name });
      })
      .catch(() => {
        /* non-essential — fall back to the cached values */
      });
    return () => {
      cancelled = true;
    };
  }, [recentId, token, activeTenant, activeWorkspace]);

  let isReturning = false;
  try {
    // Per-account key: a brand-new account on a browser where someone else had
    // flows must read "Welcome", not "Welcome back".
    isReturning =
      localStorage.getItem(
        `${HAS_FLOWS_KEY}.${userScope(activeTenant || me?.tenant, me?.subject)}`,
      ) === "1";
  } catch {
    /* private mode / strict iframe — treat as first-time */
  }

  // The zero-setup starter is a demonstration, and you only need it once.
  // Showing it to someone with a flow of their own to resume puts a toy above
  // their actual work. `recent` is checked as well as the flag because a
  // resumable flow is proof of use even where the flag never got written.
  const showDemo = !isReturning && !recent;

  return (
    <div className="welcome">
      <header className="welcome-head">
        <h1>{isReturning ? t("welcome.titleReturning") : t("welcome.title")}</h1>
        <p className="welcome-intro">
          {isReturning ? t("welcome.introReturning") : t("welcome.intro")}
        </p>
      </header>

      {recent && (
        <Link
          to={`/flows/${encodeURIComponent(recent.id)}`}
          className="welcome-resume"
        >
          <span className="welcome-resume-icon">
            <FlowIcon icon={live?.icon ?? recent.icon} size={ICON.lg} />
          </span>
          <span className="welcome-resume-body">
            <span className="welcome-resume-lede">{t("welcome.continueTitle")}</span>
            <span className="welcome-resume-name">{live?.name ?? recent.name}</span>
          </span>
          <ArrowRight size={ICON.md} className="welcome-resume-arrow" />
        </Link>
      )}

      {showDemo && (
        <Link
          to="/flows/new?tab=template&start=try-it-now"
          className="welcome-demo"
        >
          <span className="welcome-demo-icon">
            <Play size={ICON.lg} />
          </span>
          <span className="welcome-demo-body">
            <span className="welcome-demo-title">{t("welcome.demoTitle")}</span>
            {/* Inside the card and above the arrow: this is the sentence that
                tells you what the click does, so it has to arrive before the
                click, not under it. */}
            <span className="welcome-demo-desc">{t("welcome.demoDesc")}</span>
          </span>
          <ArrowRight size={ICON.md} className="welcome-demo-arrow" />
        </Link>
      )}

      <section>
        <h2 className="welcome-section-title">{t("welcome.startHeading")}</h2>
        <div className="welcome-options">
          {OPTIONS.map(({ key, to, Icon }) => (
            <Link key={key} to={to} className="welcome-option">
              <Icon size={ICON.lg} className="welcome-option-icon" />
              <span className="welcome-option-title">
                {t(`welcome.option.${key}.title`)}
              </span>
              <span className="welcome-option-desc">
                {t(`welcome.option.${key}.desc`)}
              </span>
              {key === "ai" && aiReady === false && (
                <span className="welcome-option-note">
                  {t("welcome.option.ai.needsProvider")}
                </span>
              )}
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
